const STORAGE_KEY = 'wishlist';
const WISHLIST_CHANGED_EVENT = 'thg:wishlist-changed';

type WishlistListener = (productIds: string[]) => void;

const normalizeIds = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return Array.from(
    new Set(
      value
        .filter((id): id is string => typeof id === 'string')
        .map(id => id.trim())
        .filter(Boolean)
    )
  );
};

export const getWishlist = (): string[] => {
  if (globalThis.window === undefined) {
    return [];
  }

  try {
    const raw = globalThis.window.localStorage.getItem(STORAGE_KEY);

    return normalizeIds(raw ? JSON.parse(raw) : []);
  } catch (error) {
    console.error('Could not read Wishlist from localStorage.', error);

    return [];
  }
};

export const setWishlist = (productIds: Iterable<string>): string[] => {
  const normalizedIds = normalizeIds(Array.from(productIds));

  if (globalThis.window === undefined) {
    return normalizedIds;
  }

  const currentIds = getWishlist();

  const hasChanged =
    currentIds.length !== normalizedIds.length ||
    currentIds.some((id, index) => id !== normalizedIds[index]);

  if (!hasChanged) {
    return currentIds;
  }

  try {
    globalThis.window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalizedIds));
    globalThis.window.dispatchEvent(
      new CustomEvent<string[]>(WISHLIST_CHANGED_EVENT, {
        detail: normalizedIds
      })
    );
  } catch (error) {
    console.error('Could not save Wishlist to localStorage.', error);
  }

  return normalizedIds;
};
export const isInWishlist = (productId: string): boolean => {
  const normalizedId = productId.trim();

  return Boolean(normalizedId && getWishlist().includes(normalizedId));
};

export const addToWishlist = (productId: string): boolean => {
  const normalizedId = productId.trim();

  if (!normalizedId) {
    return false;
  }

  setWishlist([...getWishlist(), normalizedId]);

  return true;
};

export const removeFromWishlist = (productId: string): void => {
  const normalizedId = productId.trim();

  if (!normalizedId) {
    return;
  }

  setWishlist(getWishlist().filter(id => id !== normalizedId));
};

export const removeWishlistItems = (productIds: Iterable<string>): void => {
  const removableIds = new Set(
    Array.from(productIds)
      .map(id => id.trim())
      .filter(Boolean)
  );

  if (removableIds.size === 0) {
    return;
  }

  setWishlist(getWishlist().filter(id => !removableIds.has(id)));
};

export const toggleWishlist = (productId: string): boolean => {
  const normalizedId = productId.trim();

  if (!normalizedId) {
    return false;
  }

  if (isInWishlist(normalizedId)) {
    removeFromWishlist(normalizedId);
    return false;
  }

  addToWishlist(normalizedId);
  return true;
};

export const subscribeToWishlist = (listener: WishlistListener): (() => void) => {
  if (globalThis.window === undefined) {
    return () => undefined;
  }

  const handleWishlistChange = (event: Event): void => {
    const customEvent = event as CustomEvent<string[]>;
    listener(customEvent.detail ?? getWishlist());
  };

  const handleStorageChange = (event: StorageEvent): void => {
    if (event.key === STORAGE_KEY) {
      listener(getWishlist());
    }
  };

  globalThis.window.addEventListener(WISHLIST_CHANGED_EVENT, handleWishlistChange);
  globalThis.window.addEventListener('storage', handleStorageChange);

  return () => {
    globalThis.window.removeEventListener(WISHLIST_CHANGED_EVENT, handleWishlistChange);
    globalThis.window.removeEventListener('storage', handleStorageChange);
  };
};
